import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { isUndefined } from 'lodash';
import Collapse from 'reactstrap/lib/Collapse';
import dateFormat from 'dateformat';
import { createDate } from 'site-modules/shared/utils/date-utils';
import { TIME } from 'client/utils/time';
import { stripHTML } from 'site-modules/shared/utils/strip-html';
import { getDealerRooftopId } from 'site-modules/shared/utils/dealer';
import { ReviewEntities } from 'client/data/models/dealer-reviews';
import { DEALER_PROPS, DEALER_COMMENT_PAGES } from 'client/constants/dealer-reviews';
import { getPlural } from 'client/utils/plural';
import { Link } from 'site-modules/shared/components/link/link';
import { TruncatedTextLessCSS } from 'site-modules/shared/components/inventory/truncated-text-less-css/truncated-text-less-css';
import { UserCommentsList } from 'site-modules/dealerships/components/user-comments-list/user-comments-list';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';

import './user-review.scss';

const RECOMMENDATION_EMOJI_MAP = {
  Yes: `👍`,
  No: `👎`,
};
const COMMENTS_LIST_ID = 'user-comments-list-id';

function hasDealerResponse(comments) {
  return comments && comments.some(comment => comment.dealerComment);
}

function ReviewLineItem({ flag, text }) {
  return flag === null ? null : (
    <li className="size-14 mb-0">
      <span className="fst-italic">{text}</span>
      <span className="ps-0_5 pe-0_25 emoji" role="img" aria-hidden>
        {RECOMMENDATION_EMOJI_MAP[flag]}
      </span>
      <span className="fw-bold">{flag}</span>
    </li>
  );
}

ReviewLineItem.propTypes = {
  flag: PropTypes.string,
  text: PropTypes.string.isRequired,
};

ReviewLineItem.defaultProps = {
  flag: null,
};

class UserReviewUI extends Component {
  state = {
    isCommentsCollapsed: true,
  };

  toggleComments = () => {
    this.setState(prevState => ({ isCommentsCollapsed: !prevState.isCommentsCollapsed }));
  };

  render() {
    const { review, params, location, index } = this.props;
    const { dealerState, dealerCity, logicalName } = params;
    const hasDealerComment = hasDealerResponse(review.comments);
    const processedLocation = !getDealerRooftopId(this.props)
      ? location.pathname?.replace('/all/', '/')
      : `/dealerships/${dealerState}/${dealerCity}/${logicalName}/`;
    const { reviewType, hasPreviousDealer } = review;
    const commentsId = `${COMMENTS_LIST_ID}-${isUndefined(index) ? 'top-review' : index}`;
    const reviewLinks = (
      <div className="size-14 text-cool-gray-40 text-start mt-1_5">
        <Link
          className="text-underline"
          to={`${processedLocation}${DEALER_COMMENT_PAGES[reviewType]}-${review.id}.html`}
          data-tracking-id="ddp_view_dealer_review_post_comment"
        >
          Post comment
        </Link>
        <span className="mx-0_75">|</span>
        <Link
          className="text-underline"
          to={`https://help.edmunds.com/hc/en-us/requests/new?ticket_form_id=2343&ticket[subject]=Report%20review%20(${
            review.id
          })%20to%20Edmunds.com%20for%20investigation&ticket[description]=I%20am%20reporting%20this%20review%20%22${
            review.title
          }%22%20posted%20by%20${review.author}%20because...`}
          data-tracking-id="ddp_view_dealer_review_report_it"
        >
          Report review
        </Link>
        <span className="mx-0_75">|</span>
        <Link
          className="text-underline"
          to={`/dealerships/${dealerState}/${dealerCity}/${logicalName}/update/${DEALER_COMMENT_PAGES[reviewType]}-${
            review.id
          }.html`}
          rel="nofollow"
        >
          Update review
        </Link>
      </div>
    );

    const showUpdatedString =
      !!review.updatedIso && createDate(review.updatedIso) - createDate(review.createdIso) > TIME.MS.FIVE_DAYS;

    return (
      <div className="dealership-user-review">
        <h3 className="user-review-title size-16">{review.title}</h3>
        <div>
          <RatingStars className="user-review-rating text-blue-50 pe-0_5" rating={review.rating} />
          <span className="size-14 text-cool-gray-40">
            by {review.author} on {dateFormat(review.createdIso, 'mm/dd/yyyy')}
          </span>
          {showUpdatedString && (
            <div className="d-inline-block small px-0_25 ms-0_25 text-white bg-primary-darker rounded">
              updated {review.updated}
            </div>
          )}
        </div>
        <p className="small mb-0_5">
          {hasPreviousDealer && <strong> - This review was published during previous ownership</strong>}
        </p>
        <TruncatedTextLessCSS
          text={stripHTML(review.text)}
          maxTextLength={240}
          btnTextMore="Show more"
          btnTextLess="Show less"
          btnClassName="btn btn-link size-16 text-transform-none text-underline fw-normal px-0 pb-0 pt-0_25"
          className="review-text"
          withArrowIcon
        >
          <ul className="list-unstyled my-1">
            <ReviewLineItem flag={review.isDealerRecommended} text="Recommend this dealer?" />
            <ReviewLineItem flag={review.wasCarBoughtHere} text="Purchased a vehicle from this dealer?" />
            <ReviewLineItem
              flag={review.wereYouSatisfied}
              text="Were you satisfied with the quality of work performed on your vehicle?"
            />
            <ReviewLineItem
              flag={review.didDealerCompleteWork}
              text="Did the dealership complete the work within the timeframe promised?"
            />
            <ReviewLineItem
              flag={review.didDealerProvideQuote}
              text="Did the dealership provide you with an accurate quote of the work to be performed?"
            />
            <ReviewLineItem
              flag={review.wasPriceAppropriate}
              text="Do you feel the price paid for service(s) was fair and appropriate?"
            />
            <ReviewLineItem
              flag={review.didDealerAllCommitmentsMade}
              text="Did the dealer honor all commitments made?"
            />
          </ul>
        </TruncatedTextLessCSS>

        {!review.comments.length && reviewLinks}
        {!!review.comments.length && (
          <>
            <div className="mt-1_5">
              <div className="d-flex justify-content-between align-items-center">
                <button
                  className="btn btn-link px-0 py-0 text-cool-gray-30 text-transform-none fw-normal size-16"
                  onClick={this.toggleComments}
                  aria-expanded={!this.state.isCommentsCollapsed}
                  aria-controls={commentsId}
                >
                  <i className="icon-bubble2 pe-0_25" aria-hidden />
                  <span className="text-underline">
                    {this.state.isCommentsCollapsed ? review.comments.length : 'Hide'}{' '}
                    {getPlural('comment', review.comments.length)}
                    {!this.state.isCommentsCollapsed && <i className="small ps-0_25 icon-arrow-up3" aria-hidden />}
                  </span>
                </button>

                {hasDealerComment && this.state.isCommentsCollapsed && (
                  <button
                    className="btn btn-link border-0 dealer-response-label text-cool-gray-30 fw-medium size-14 text-transform-none px-0_5 py-0_5 bg-cool-gray-80 rounded-16 d-flex align-items-center"
                    onClick={this.toggleComments}
                    aria-expanded={!this.state.isCommentsCollapsed}
                    aria-controls={commentsId}
                  >
                    <i className="icon-checkmark-circle text-success pe-0_5 size-16" aria-hidden />
                    <span>Dealer response</span>
                  </button>
                )}
              </div>
            </div>
            <Collapse id={commentsId} isOpen={!this.state.isCommentsCollapsed}>
              <UserCommentsList comments={review.comments} />
            </Collapse>
            {reviewLinks}
          </>
        )}
      </div>
    );
  }
}

UserReviewUI.propTypes = {
  review: ReviewEntities.ReviewEntity.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  params: DEALER_PROPS,
  index: PropTypes.number,
};

UserReviewUI.defaultProps = {
  params: {},
  location: {},
  index: undefined,
};

export const mapStateToProps = state => ({
  location: state.pageContext.location,
});

export const UserReview = connect(mapStateToProps)(UserReviewUI);
